import { PackageEvidence } from '@cadstrom/cadstrom-shared';
import ThumbDownAltRoundedIcon from '@mui/icons-material/ThumbDownAltRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';

import type { ReactNode } from 'react';

/** A stance the viewer can hold on a piece of evidence. */
export type EvidenceStandingVote =
	| PackageEvidence.EvidenceVotePolarity.For
	| PackageEvidence.EvidenceVotePolarity.Against;

/** What each stance answers to the question the vote is about, as a self-contained statement. */
export type EvidenceVoteAnswers = Readonly<Record<EvidenceStandingVote, string>>;

/** Props for {@link EvidenceVoteButtons}. */
export interface EvidenceVoteButtonsProps {
	/** Each button's label: its answer to the question the vote is about. */
	answers: EvidenceVoteAnswers;
	/** The viewer's current stance, or `null` when they hold none. */
	myVote: EvidenceStandingVote | null;
	/** Blocks voting, e.g. while a vote is being saved; the buttons stay focusable but ignore presses. */
	disabled?: boolean;
	/** Called with the stance the viewer chose; `Withdrawn` when they retract their current vote. */
	onVote: (polarity: PackageEvidence.EvidenceVotePolarity) => void;
}

interface VoteOption {
	polarity: EvidenceStandingVote;
	icon: ReactNode;
	pressedClassName: string;
}

const VOTE_OPTIONS: readonly VoteOption[] = [
	{
		polarity: PackageEvidence.EvidenceVotePolarity.For,
		icon: <ThumbUpAltRoundedIcon aria-hidden style={{ fontSize: 15 }} />,
		pressedClassName: 'text-blue-11',
	},
	{
		polarity: PackageEvidence.EvidenceVotePolarity.Against,
		icon: <ThumbDownAltRoundedIcon aria-hidden style={{ fontSize: 15 }} />,
		pressedClassName: 'text-red-11',
	},
];

/** A voted button keeps its colour without a hover change. */
const UNPRESSED_CLASS = 'text-gray-a8 hover:text-gray-a11';

/**
 * Yes and no toggles answering a question about one region, showing only the viewer's own stance. Pressing the
 * viewer's current vote again withdraws it; pressing the other one switches to it.
 */
export function EvidenceVoteButtons({ answers, myVote, disabled = false, onVote }: EvidenceVoteButtonsProps) {
	return (
		<div className='inline-flex items-center gap-0.5'>
			{VOTE_OPTIONS.map(({ polarity, icon, pressedClassName }) => {
				const isPressed = myVote === polarity;
				const actionLabel = isPressed ? 'Remove your vote' : answers[polarity];

				return (
					<Tooltip key={polarity} title={actionLabel}>
						{/* Marked rather than truly disabled so the pressed button keeps keyboard focus while blocked. */}
						<ButtonBase
							aria-label={actionLabel}
							aria-pressed={isPressed}
							aria-disabled={disabled}
							disableRipple={disabled}
							onClick={() => {
								if (!disabled) {
									onVote(isPressed ? PackageEvidence.EvidenceVotePolarity.Withdrawn : polarity);
								}
							}}
							className={clsx(
								'inline-flex size-6 items-center justify-center rounded transition-colors',
								'focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-blue-9',
								isPressed ? pressedClassName : UNPRESSED_CLASS,
								disabled && 'cursor-default opacity-50',
							)}
						>
							{icon}
						</ButtonBase>
					</Tooltip>
				);
			})}
		</div>
	);
}
